const currencyById = {
  1: "USD",
  2: "LBP",
  178: "EUR",
};

export const dateOptions = {
  day: "2-digit",
  hour: "2-digit",
  hour12: true,
  minute: "2-digit",
  month: "short",
  year: "numeric",
};

function isEmptyValue(value) {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  );
}

function createRow(label, value, options = {}) {
  if (isEmptyValue(value)) return null;

  return {
    key: options.key || label,
    sourceKeys: options.sourceKeys || [options.key || label],
    label,
    value,
  };
}

function createDateRow(label, value, options = {}) {
  if (isEmptyValue(value)) return null;

  return createRow(
    label,
    {
      type: "date",
      value,
    },
    options,
  );
}

function createLinkRow(label, text, href, options = {}) {
  if (isEmptyValue(text) || isEmptyValue(href)) return null;

  return createRow(
    label,
    {
      type: "link",
      href,
      text,
    },
    options,
  );
}

export function formatTransactionAmount(value, currencyId) {
  if (isEmptyValue(value)) return "-";

  const amount = Number(value);
  if (Number.isNaN(amount)) return value;

  const currency = currencyById[currencyId];

  if (currency === "USD" || currency === "EUR") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  if (currency === "LBP") {
    return `L.L ${new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(amount)}`;
  }

  return new Intl.NumberFormat("en-US").format(amount);
}

export function formatTransferAbroadAmount(value, details) {
  if (isEmptyValue(value)) return "-";

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    const symbol = details?.CurrencySymbol || "";
    return symbol ? `${symbol} ${value}` : value;
  }

  const symbol = details?.CurrencySymbol || "";
  const currencyCode = details?.Currency;
  const decimals = currencyCode === "LBP" ? 0 : 2;
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);

  if (symbol) return `${symbol} ${formatted}`;
  if (currencyCode) return `${currencyCode} ${formatted}`;
  return formatted;
}

function createAmountRow(label, value, currencyId, options = {}) {
  if (isEmptyValue(value)) return null;
  return createRow(label, formatTransactionAmount(value, currencyId), options);
}

function createTransferAbroadAmountRow(label, value, details, options = {}) {
  if (isEmptyValue(value)) return null;
  return createRow(label, formatTransferAbroadAmount(value, details), options);
}

function createSection(title, rows, options = {}) {
  const safeRows = (rows || []).filter(Boolean);
  if (!safeRows.length) return null;

  return {
    key: options.key || title,
    title,
    rows: safeRows,
  };
}

function firstValue(details, keys = []) {
  for (const key of keys) {
    const value = details?.[key];
    if (!isEmptyValue(value)) return value;
  }
  return undefined;
}

function normalizeKey(value) {
  return String(value || "")
    .replace(/[\s_]/g, "")
    .toLowerCase();
}

function applyHiddenKeys(sections, hiddenKeys = []) {
  const hiddenSet = new Set(hiddenKeys.map(normalizeKey));

  return (sections || [])
    .filter(Boolean)
    .map((section) => ({
      ...section,
      rows: (section.rows || []).filter((row) => {
        const rowKeys = Array.isArray(row.sourceKeys)
          ? row.sourceKeys
          : [row.key];

        return !rowKeys.some((key) => hiddenSet.has(normalizeKey(key)));
      }),
    }))
    .filter((section) => section.rows.length > 0);
}

function getTransactionKind(details, variant, feature) {
  if (variant === "transfer-abroad") return "transfer-abroad";
  if (feature === "payroll") return "payroll";

  const transactionType = String(details?.TransactionType || "").toLowerCase();

  if (details?.DesignId === 3 || details?.DeliveryService) {
    return "butler";
  }

  if (transactionType.includes("payroll")) return "payroll";
  if (transactionType.includes("quick pay")) return "quick-pay";
  if (transactionType.includes("suyool to suyool")) return "s2s";
  if (
    transactionType.includes("outward transfer") ||
    transactionType.includes("bank transfer")
  ) {
    return "bank-transfer";
  }
  if (
    transactionType.includes("cash deposit") ||
    transactionType.includes("cash withdrawal")
  ) {
    return "cash";
  }
  if (transactionType.includes("exchange")) return "exchange";
  if (transactionType.includes("card")) return "card";
  return "generic";
}

function buildCommonRows(details) {
  const auditLabel =
    String(details?.TransactionType || "").toLowerCase().includes("cash")
      ? "Created by"
      : "Issued By";

  return [
    createRow("Transaction ID", details?.TransactionID, {
      key: "TransactionID",
    }),
    createDateRow("Date", details?.Date, { key: "Date" }),
    createDateRow("Expiry Date", details?.DateExpiry, { key: "DateExpiry" }),
    createRow(
      "Reason",
      firstValue(details, ["ReasonForTransfer", "Reason"]),
      {
        sourceKeys: ["ReasonForTransfer", "Reason"],
      },
    ),
    createLinkRow(
      "File",
      firstValue(details, ["InvoiceDocument", "FileName"]),
      firstValue(details, ["InvoiceDocumentUrl", "FileUrl"]),
      {
        sourceKeys: [
          "InvoiceDocument",
          "InvoiceDocumentUrl",
          "FileName",
          "FileUrl",
        ],
      },
    ),
    createRow(
      auditLabel,
      firstValue(details, ["IssuedBy", "InitiatorName"]),
      {
        sourceKeys: ["IssuedBy", "InitiatorName"],
      },
    ),
    Number(details?.Fees) > 0
      ? createAmountRow("Fees", details?.Fees, details?.CurrencyID, {
          key: "Fees",
        })
      : null,
  ];
}

function buildPayrollSections(details) {
  return [
    createSection("Overview", [
      Number(details?.TotalEmployees) > 0
        ? createRow("Total Employees", details.TotalEmployees, {
            key: "TotalEmployees",
          })
        : null,
      Number(details?.TotalTransactions) > 0
        ? createRow("Total Transactions", details.TotalTransactions, {
            key: "TotalTransactions",
          })
        : null,
      Number(details?.EmployeeCount) > 0
        ? createRow("Employee Count", details.EmployeeCount, {
            key: "EmployeeCount",
          })
        : null,
      Number(details?.Eligible) > 0
        ? createRow("Eligible", details.Eligible, { key: "Eligible" })
        : null,
      Number(details?.Added) > 0
        ? createRow("Added", details.Added, { key: "Added" })
        : null,
      Number(details?.Edited) > 0
        ? createRow("Edited", details.Edited, { key: "Edited" })
        : null,
      Number(details?.Disabled) > 0
        ? createRow("Disabled", details.Disabled, { key: "Disabled" })
        : null,
    ]),
    createSection("Details", [
      createRow(
        "Recipient Name",
        firstValue(details, ["RecipientName", "BeneficiaryName", "Recipient"]),
        {
          sourceKeys: ["RecipientName", "BeneficiaryName", "Recipient"],
        },
      ),
      createRow(
        "Recipient Mobile No",
        firstValue(details, ["RecipientMobileNo", "PhoneNumber"]),
        {
          sourceKeys: ["RecipientMobileNo", "PhoneNumber"],
        },
      ),
      ...buildCommonRows(details),
    ]),
  ];
}

function buildS2SSections(details) {
  return [
    createSection("Details", [
      createRow(
        "Recipient",
        firstValue(details, ["RecipientName", "BeneficiaryName", "Recipient"]),
        {
          sourceKeys: ["RecipientName", "BeneficiaryName", "Recipient"],
        },
      ),
      createRow(
        "Phone Number",
        firstValue(details, ["RecipientMobileNo", "PhoneNumber"]),
        {
          sourceKeys: ["RecipientMobileNo", "PhoneNumber"],
        },
      ),
      ...buildCommonRows(details),
    ]),
  ];
}

function buildBankTransferSections(details) {
  return [
    createSection("Details", [
      createRow(
        "Beneficiary Name",
        firstValue(details, ["BeneficiaryName", "RecipientName", "Recipient"]),
        {
          sourceKeys: ["BeneficiaryName", "RecipientName", "Recipient"],
        },
      ),
      createRow(
        "IBAN Number",
        firstValue(details, ["IBAN", "Description"]),
        {
          sourceKeys: ["IBAN", "Description"],
        },
      ),
      createRow(
        "Phone Number",
        firstValue(details, ["PhoneNumber", "RecipientMobileNo"]),
        {
          sourceKeys: ["PhoneNumber", "RecipientMobileNo"],
        },
      ),
      ...buildCommonRows(details),
    ]),
  ];
}

function buildCashSections(details) {
  return [
    createSection("Details", [
      createRow(
        "Person in Charge",
        firstValue(details, ["PersonInCharge", "ReceiverName"]),
        {
          sourceKeys: ["PersonInCharge", "ReceiverName"],
        },
      ),
      createRow(
        "Mobile Number",
        firstValue(details, ["PhoneNumber", "MobileNumber"]),
        {
          sourceKeys: ["PhoneNumber", "MobileNumber"],
        },
      ),
      // createRow("Sub Title", details?.SubTitle, { key: "SubTitle" }),
      createRow("Merchant Name", details?.MerchantName, { key: "MerchantName" }),

      createRow("Payment Reference", details?.PaymentReference, {
        key: "PaymentReference",
      }),
      ...buildCommonRows(details),
    ]),
    createSection("Schedule", [
      createDateRow("Preferred Date & Time", details?.DatePreferred, {
        key: "DatePreferred",
      }),
      createRow("Delivery Location", details?.DeliveryLocation, {
        key: "DeliveryLocation",
      }),
      createDateRow("Delivered Date & Time", details?.DateDelivery, {
        key: "DateDelivery",
      }),
    ]),
  ];
}

function buildButlerSections(details) {
  return [
    createSection("Request", [
      createDateRow("Date Requested", details?.Date, { key: "Date" }),
      createRow("Payment Reference", details?.PaymentReference, {
        key: "PaymentReference",
      }),
      createLinkRow(
        "Invoice/ Document",
        details?.InvoiceDocument,
        details?.InvoiceDocumentUrl,
        {
          sourceKeys: ["InvoiceDocument", "InvoiceDocumentUrl"],
        },
      ),
    ]),
    createSection("Recipient", [
      createRow(
        "Person in Charge",
        firstValue(details, ["PersonInCharge", "ReceiverName"]),
        {
          sourceKeys: ["PersonInCharge", "ReceiverName"],
        },
      ),
      createRow(
        "Mobile Number",
        firstValue(details, ["MobileNumber", "PhoneNumber"]),
        {
          sourceKeys: ["MobileNumber", "PhoneNumber"],
        },
      ),
      createRow("Email", details?.Email, { key: "Email" }),
      // createRow("Sub Title", details?.SubTitle, { key: "SubTitle" }),
    ]),
    createSection("Delivery", [
      createDateRow("Preferred Date & Time", details?.DatePreferred, {
        key: "DatePreferred",
      }),
      createRow("Delivery Location", details?.DeliveryLocation, {
        key: "DeliveryLocation",
      }),
      createDateRow("Delivered Date & Time", details?.DateDelivery, {
        key: "DateDelivery",
      }),
      Number(details?.Fees) > 0
        ? createAmountRow("Fees", details?.Fees, details?.CurrencyID, {
            key: "Fees",
          })
        : null,
    ]),
  ];
}

function buildExchangeSections(details) {
  return [
    createSection("Details", [
      ...buildCommonRows(details),
      createRow("Exchange Rate", details?.ExchangeRate, {
        key: "ExchangeRate",
      }),
      createAmountRow(
        "Exchanged Amount",
        details?.ExchangedAmount,
        details?.CurrencyID,
        {
          key: "ExchangedAmount",
        },
      ),
      createAmountRow(
        "Cross Border Fees",
        details?.crossBorderFees,
        details?.CurrencyID,
        {
          key: "crossBorderFees",
        },
      ),
      createRow("Exchange Type", details?.Type, { key: "Type" }),
      createRow("Client", details?.Client, { key: "Client" }),
    ]),
  ];
}

function buildCardSections(details) {
  return [
    createSection("Details", [
      createRow("Cardholder Name", details?.CardHolderName, {
        key: "CardHolderName",
      }),
      createRow("Card Last Digits", details?.CardLastDigits, {
        key: "CardLastDigits",
      }),
      createRow(
        "Merchant Name",
        firstValue(details, ["MerchantName", "Merchant"]),
        {
          sourceKeys: ["MerchantName", "Merchant"],
        },
      ),
      createRow("Payment Type", details?.PaymentType, { key: "PaymentType" }),
      createRow("Branch", details?.Branch, { key: "Branch" }),
      createRow("Location", details?.Location, { key: "Location" }),
      createRow("Region", details?.Region, { key: "Region" }),
      ...buildCommonRows(details),
    ]),
  ];
}

const genericFieldConfig = [
  {
    label: "Beneficiary",
    sourceKeys: ["BeneficiaryName", "RecipientName", "Recipient"],
  },
  {
    label: "Merchant Name",
    sourceKeys: ["MerchantName", "Merchant"],
  },
  {
    label: "Phone Number",
    sourceKeys: ["RecipientMobileNo", "PhoneNumber", "MobileNumber"],
  },
  {
    label: "IBAN",
    sourceKeys: ["IBAN"],
  },
  {
    label: "Description",
    sourceKeys: ["Description"],
  },
  {
    label: "User Name",
    sourceKeys: ["UserName"],
  },
  {
    label: "Client",
    sourceKeys: ["Client"],
  },
  {
    label: "Exchange Type",
    sourceKeys: ["Type"],
  },
  {
    label: "Service Provider",
    sourceKeys: ["ServiceProvider"],
  },
];

function buildGenericSections(details) {
  const configRows = genericFieldConfig.map((item) =>
    createRow(item.label, firstValue(details, item.sourceKeys), {
      sourceKeys: item.sourceKeys,
    }),
  );

  return [
    createSection("Details", [...configRows, ...buildCommonRows(details)]),
  ];
}

function buildTransferAbroadSections(details) {
  const detailsSection = details?.Details || {};
  const transactionType = details?.TransactionType || detailsSection?.Type || "";
  const isTransferToCard = String(transactionType)
    .toLowerCase()
    .includes("card");

  const topRight = isTransferToCard
    ? {
        label: "Card Number",
        value: detailsSection["Card Number"] || "-",
      }
    : {
        label: "Account Number / IBAN",
        value:
          detailsSection["Account Number / IBAN"] ||
          detailsSection["Account Number"] ||
          detailsSection["IBAN Number"] ||
          "-",
      };

  const detailRows = Object.entries(detailsSection)
    .filter(([key, value]) => {
      if (isEmptyValue(value)) return false;
      if (key === topRight.label) return false;
      if (
        topRight.label === "Account Number / IBAN" &&
        ["Account Number", "IBAN Number"].includes(key)
      ) {
        return false;
      }
      return true;
    })
    .map(([key, value]) =>
      normalizeKey(key).includes("date")
        ? createDateRow(
            key === "Remittance Purpose" ? "Reason of transfer" : key,
            value,
            { key },
          )
        : createRow(
            key === "Remittance Purpose" ? "Reason of transfer" : key,
            value,
            { key },
          ),
    )
    .filter(Boolean);

  return {
    overviewRows: [
      createRow("Beneficiary Name", details?.Beneficiary, {
        key: "Beneficiary",
      }),
      topRight?.value && topRight.value !== "-"
        ? createRow(topRight.label, topRight.value, {
            key: topRight.label,
          })
        : null,
    ].filter(Boolean),
    detailsRows: detailRows,
    amountRows: [
      createTransferAbroadAmountRow(
        "Amount to Be Sent",
        details?.Amount,
        details,
        { key: "Amount" },
      ),
      createTransferAbroadAmountRow(
        "Amount the Recipient Will Receive",
        details?.AmountReceived,
        details,
        { key: "AmountReceived" },
      ),
    ].filter(Boolean),
  };
}

export function buildTransactionDetailsView(
  details,
  { variant = "default", feature = "", hiddenKeys = [] } = {},
) {
  if (!details) {
    return {
      kind: variant === "transfer-abroad" ? "transfer-abroad" : "default",
      sections: [],
      transferAbroad: null,
    };
  }

  const kind = getTransactionKind(details, variant, feature);

  if (kind === "transfer-abroad") {
    return {
      kind,
      sections: [],
      transferAbroad: buildTransferAbroadSections(details),
    };
  }

  const sectionsByKind = {
    "bank-transfer": buildBankTransferSections,
    butler: buildButlerSections,
    card: buildCardSections,
    cash: buildCashSections,
    exchange: buildExchangeSections,
    payroll: buildPayrollSections,
    "quick-pay": buildGenericSections,
    s2s: buildS2SSections,
    generic: buildGenericSections,
  };

  const builder = sectionsByKind[kind] || buildGenericSections;

  return {
    kind,
    sections: applyHiddenKeys(builder(details), hiddenKeys),
    transferAbroad: null,
  };
}
